<script lang="ts" setup>
import { ToccoSeminarFieldsFragment } from '~/api/graphql/generated'
import { dedupeSeminarModules, ToccoSeminarModuleItem } from '~/api/drupal'
import { normalizeDurationDays } from '~/lib/utils/toccoUtils'

type BaseModuleReferences =
  ToccoSeminarFieldsFragment['fieldRequiredBaseModules'] | ToccoSeminarFieldsFragment['fieldOptionalBaseModules']

const props = defineProps<{
  entity: ToccoSeminarFieldsFragment
}>()

const { t } = useI18n()
const localePath = useLocalePath()

const overlayController = useModal()
const selectedModuleId = ref<string | null>(null)

const mapModules = (references: BaseModuleReferences, optional: boolean): ToccoSeminarModuleItem[] =>
  (references ?? [])
    .map((reference) => reference?.entity)
    .filter(
      (module): module is NonNullable<typeof module> =>
        module?.__typename === 'ToccoBaseModule' && Boolean(module.status),
    )
    .sort((a, b) => (a.fieldToccoAbbreviation ?? '').localeCompare(b.fieldToccoAbbreviation ?? ''))
    .map((module) => ({
      id: (module.id ?? '').toString(),
      title: module.title ?? '',
      durationDays: normalizeDurationDays(module.fieldDurationDays),
      optional,
    }))

const modules = computed(() =>
  dedupeSeminarModules(
    mapModules(props.entity.fieldRequiredBaseModules, false),
    mapModules(props.entity.fieldOptionalBaseModules, true),
  ),
)

const title = computed(() => props.entity.fieldModuleSectionTitle ?? t('tocco.moduleOverview.title'))
const ctaLabel = computed(() => props.entity.fieldOverviewCtaLabel || t('tocco.moduleOverview.cta'))
const ctaAnchor = computed(() => props.entity.fieldOverviewCtaAnchor?.trim().replace(/^#/, '') || '')
const ctaTarget = computed(() =>
  ctaAnchor.value ? { hash: `#${ctaAnchor.value}` } : localePath(`/registration/seminar/${props.entity.id}`),
)

const openModule = (id: string) => {
  selectedModuleId.value = id
  overlayController.open()
}
</script>

<template>
  <SectionSpecial v-if="modules.length" :title="title">
    <div class="modules">
      <ToccoModuleCard
        v-for="module in modules"
        :key="module.id"
        :title="module.title"
        :duration-days="module.durationDays"
        :optional="module.optional"
        @click="openModule(module.id)"
      />
    </div>

    <Button class="cta" primary slide :to="ctaTarget" :title="ctaLabel">
      {{ ctaLabel }}
    </Button>

    <ModuleOverlay :controller="overlayController" :module-id="selectedModuleId" />
  </SectionSpecial>
</template>

<style lang="scss" scoped>
.modules {
  display: grid;
  grid-template-columns: 100%;
  gap: 16px;

  @include breakpoint(ts) {
    grid-template-columns: 1fr 1fr;
  }
  @include breakpoint(tl) {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.cta {
  @include fluid(margin-top, 30px, 48px);
}
</style>
